<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import CompanyAPI from 'dashboard/api/companies';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  modelValue: { type: Number, default: null },
  selectedName: { type: String, default: '' },
  showNoneOption: { type: Boolean, default: true },
  placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const companies = ref([]);
const selectedCompany = ref(null);

const options = computed(() => {
  const list = [...companies.value];
  if (
    selectedCompany.value &&
    !list.some(c => c.id === selectedCompany.value.id)
  ) {
    list.unshift(selectedCompany.value);
  }
  const companyOptions = list.map(company => ({
    label: company.name,
    value: company.id,
  }));
  if (!props.showNoneOption) return companyOptions;

  return [
    { label: t('COMPANIES.SELECT.NONE'), value: null },
    ...companyOptions,
  ];
});

const fetchCompanies = async (query = '') => {
  const { data } = query
    ? await CompanyAPI.search(query)
    : await CompanyAPI.get();
  companies.value = data.payload;
};

const syncSelectedCompany = async id => {
  if (!id) {
    selectedCompany.value = null;
    return;
  }
  if (selectedCompany.value?.id === id) return;

  const known = companies.value.find(company => company.id === id);
  if (known || props.selectedName) {
    selectedCompany.value = known || { id, name: props.selectedName };
    return;
  }
  const { data } = await CompanyAPI.show(id);
  selectedCompany.value = data.payload;
};

const onSearch = debounce(query => fetchCompanies(query.trim()), 300);

const onSelect = id => {
  selectedCompany.value =
    companies.value.find(company => company.id === id) || null;
  emit('update:modelValue', id);
};

watch(() => props.modelValue, syncSelectedCompany);

onMounted(async () => {
  await fetchCompanies();
  syncSelectedCompany(props.modelValue);
});
</script>

<template>
  <ComboBox
    :model-value="modelValue"
    :options="options"
    :placeholder="placeholder || t('COMPANIES.SELECT.PLACEHOLDER')"
    :search-placeholder="t('COMPANIES.SEARCH_PLACEHOLDER')"
    :empty-state="t('COMPANIES.EMPTY_STATE.TITLE')"
    use-api-results
    @search="onSearch"
    @update:model-value="onSelect"
  />
</template>
